import { useDispatch, useSelector } from "react-redux";
import { Badge, LoadingSpinner, Card, Alert, Tabs } from 'components/common';
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import PropTypes from 'prop-types';
import { FiCopy, FiLock, FiTag, FiShield, FiSettings, FiEye, FiCheckCircle, FiCheck, FiX, FiXCircle } from 'react-icons/fi';
import { getApplication } from "redux/integration/applicationSlice";
import ApplicationDetailHeader from "./ApplicationDetailHeader";
import ImpersonationPanel from "./ImpersonationPanel";
import ImpersonationBadge from "./impersonation";
import SecretText from "components/common/SecretText";
import { toast } from "react-toastify";

// ── Field row ─────────────────────────────────────────────────────────────────
const FieldRow = ({ label, value, isCopyable, description }) => {
  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(String(value));
      toast.success('Copied to clipboard');
    }
  };
  return (
    <div>
      <p className="tw-text-[11px] tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-mb-1.5">
        {label}
      </p>
      <div className="tw-flex tw-items-center tw-gap-2 tw-group/fv">
        <p className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-break-all tw-flex-1">
          {value ? String(value) : <span className="tw-text-slate-300 tw-font-normal">—</span>}
        </p>
        {isCopyable && value && (
          <button
            onClick={handleCopy}
            title="Copy"
            className="tw-opacity-0 group-hover/fv:tw-opacity-100 tw-p-1 tw-rounded-sm tw-text-slate-400 hover:tw-text-slate-700 hover:tw-bg-slate-100 tw-transition-all tw-border-0 tw-bg-transparent tw-cursor-pointer tw-flex-shrink-0"
          >
            <FiCopy size={13} />
          </button>
        )}
      </div>
      {description && <p className="tw-text-xs tw-text-slate-400 tw-mt-1">{description}</p>}
    </div>
  );
};
FieldRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isCopyable: PropTypes.bool,
  description: PropTypes.string,
};

const ApplicationDetail = () => {
  const dispatch = useDispatch();
  const params   = useParams();
  const { applicationData: record, loading } = useSelector(s => s.integration_application);

  useEffect(() => { dispatch(getApplication(params.id)); }, [params.id, dispatch]);

  // Until the record in the store IS the one in the URL, this page would be
  // showing another client's secret and another client's impersonation policy —
  // with a Save button wired to that other client's id.
  const stale = String(record?.id || '') !== String(params.id);

  if (loading || stale) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // ── Tab panels ──────────────────────────────────────────────────────────────
  const overviewPanel = (
    <div className="tw-space-y-6">
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-10 tw-gap-y-6">
        <FieldRow
          label="Application Name"
          value={record?.name}
          description="The public name of this OAuth application"
        />
        <FieldRow
          label="Application ID"
          value={record?.id}
          isCopyable
          description="Unique identifier for this application"
        />
        <div>
          <p className="tw-text-[11px] tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-mb-1.5">
            Token impersonation
          </p>
          <div className="tw-flex tw-items-center tw-gap-2">
            <ImpersonationBadge record={record} deniedLabel="Impersonation denied" />
          </div>
          <p className="tw-text-xs tw-text-slate-400 tw-mt-1">
            Whose token this client may ask for — change it under Security
          </p>
        </div>
      </div>
      <Alert
        type="info"
        title="About This Application"
        message="This OAuth 2.0 application is configured for secure authentication. Keep your credentials safe and never expose them in public repositories."
      />
    </div>
  );

  const configPanel = (
    <div className="tw-space-y-6">
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-x-10 tw-gap-y-6">
        <FieldRow
          label="Application UID"
          value={record?.uid}
          isCopyable
          description="Unique identifier used in OAuth flow"
        />
        <FieldRow
          label="Redirect URI"
          value={record?.redirect_uri}
          isCopyable
          description="Callback URL for OAuth redirects"
        />
        <FieldRow
          label="Scopes"
          value={record?.scopes || 'Not specified'}
          description="API access permissions granted"
        />
      </div>

      <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-divide-y tw-divide-slate-100">
        <div className="tw-px-5 tw-py-3.5 tw-flex tw-items-center tw-gap-2">
          <FiTag size={13} className="tw-text-slate-400" />
          <p className="tw-text-sm tw-font-semibold tw-text-slate-700">Configuration tips</p>
        </div>
        {[
          'Ensure Redirect URI exactly matches your application callback endpoint.',
          'Request only the minimum scopes your application actually needs.',
          'Always use HTTPS for OAuth endpoints in production.',
        ].map((tip, i) => (
          <div key={i} className="tw-px-5 tw-py-2.5 tw-flex tw-items-start tw-gap-2.5">
            <FiCheckCircle size={13} className="tw-text-emerald-500 tw-mt-0.5 tw-flex-shrink-0" />
            <p className="tw-text-sm tw-text-slate-600">{tip}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const securityPanel = (
    <div className="tw-space-y-5">
      {/* Client Secret */}
      <div className="tw-rounded-lg tw-border tw-border-red-200 tw-overflow-hidden">
        <div className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-3 tw-bg-red-50 tw-border-b tw-border-red-200">
          <FiLock size={13} className="tw-text-red-600" />
          <p className="tw-text-sm tw-font-semibold tw-text-red-800 tw-mb-0">Client Secret</p>
        </div>
        <div className="tw-px-5 tw-py-4 tw-bg-white">
          <p className="tw-text-xs tw-text-slate-500 tw-mb-3">
            Confidential — never share this value publicly or commit it to source control.
          </p>
          <SecretText defaultSecret={record?.secret} />
        </div>
      </div>

      <ImpersonationPanel record={record} />

      <Alert
        type="error"
        title="Critical Security Notice"
        message="Never commit your client secret to version control, expose it in client-side code, or share it in emails. Use environment variables in production. Rotate immediately if compromised."
      />

      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
        {/* Recommended */}
        <div className="tw-rounded-lg tw-border tw-border-emerald-200 tw-overflow-hidden">
          <div className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-3 tw-bg-emerald-50 tw-border-b tw-border-emerald-200">
            <FiCheckCircle size={13} className="tw-text-emerald-500" />
            <p className="tw-text-sm tw-font-semibold tw-text-emerald-800 tw-mb-0">Recommended</p>
          </div>
          {[
            'Store secrets in environment variables',
            'Use HTTPS for all connections',
            'Rotate secrets regularly',
            'Audit access logs periodically',
          ].map((t, i) => (
            <div key={i} className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-py-2.5 tw-border-b last:tw-border-0 tw-border-emerald-50 tw-bg-white">
              <FiCheck size={12} className="tw-text-emerald-500 tw-flex-shrink-0" />
              <p className="tw-text-sm tw-text-slate-600 tw-mb-0">{t}</p>
            </div>
          ))}
        </div>

        {/* Avoid */}
        <div className="tw-rounded-lg tw-border tw-border-red-200 tw-overflow-hidden">
          <div className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-3 tw-bg-red-50 tw-border-b tw-border-red-200">
            <FiXCircle size={13} className="tw-text-red-500" />
            <p className="tw-text-sm tw-font-semibold tw-text-red-800 tw-mb-0">Avoid</p>
          </div>
          {[
            'Hardcode secrets in source code',
            'Share via email or messaging',
            'Log secret values anywhere',
            'Use secrets in frontend code',
          ].map((t, i) => (
            <div key={i} className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-py-2.5 tw-border-b last:tw-border-0 tw-border-red-50 tw-bg-white">
              <FiX size={12} className="tw-text-red-400 tw-flex-shrink-0" />
              <p className="tw-text-sm tw-text-red-600 tw-mb-0">{t}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const tabs = [
    { label: 'Overview',      icon: <FiEye      size={14} />, content: overviewPanel  },
    { label: 'Configuration', icon: <FiSettings  size={14} />, content: configPanel    },
    { label: 'Security',      icon: <FiShield    size={14} />, content: securityPanel  },
  ];

  return (
    <div className="tw-space-y-4">
      <ApplicationDetailHeader record={record} />

      {/* ── Identity card ── */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-px-6 tw-py-5">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-flex-wrap">
          <div className="tw-flex tw-items-center tw-gap-4">
            <div className="tw-w-14 tw-h-14 tw-rounded-lg tw-bg-gradient-to-br tw-from-blue-500 tw-to-primary-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-shadow-sm">
              <span className="tw-text-sm tw-font-black tw-text-white">#{record?.id}</span>
            </div>
            <div>
              <h1 className="tw-text-xl tw-font-bold tw-text-slate-900 tw-m-0 tw-mb-2 tw-leading-tight">
                {record?.name || '—'}
              </h1>
              <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-500">
                  <FiCheckCircle size={12} className="tw-text-blue-500" />
                  OAuth 2.0 Application
                </span>
                {record?.created_date && (
                  <>
                    <span className="tw-text-slate-200">·</span>
                    <span className="tw-text-xs tw-text-slate-400">Created {record.created_date}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
            <Badge variant="success" size="sm">Active</Badge>
            <ImpersonationBadge record={record} deniedLabel="Impersonation denied" />
            {record?.confidential !== undefined && (
              <Badge variant={record.confidential ? 'warning' : 'default'} size="sm">
                {record.confidential ? '🔒 Confidential' : '🔓 Public'}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <Card padding="none" shadow bordered className="tw-overflow-hidden">
        <Tabs
          tabs={tabs}
          variant="underline"
          navClassName="tw-px-5 tw-bg-slate-50/70"
          contentClassName="tw-px-6 tw-py-6"
        />
      </Card>
    </div>
  );
};

export default ApplicationDetail;
